import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import PresetFilterModal from '../../components/modals/PresetFilterModal';
import MessageSequenceDisplay from '../../components/campaigns/MessageSequenceDisplay';

interface Campaign {
  id: string;
  name: string;
  status: 'draft' | 'submitted' | 'preparing' | 'running' | 'completed';
  created_at: string;
  submitted_at: string | null;
  total_leads: number;
  service_tier: 'junior_sdr' | 'top_qualifier';
  lead_source_type?: string;
  target_audience_type?: string;
  lead_source_id?: string;
  users: {
    id: string;
    full_name: string;
    email: string;
  };
  campaign_sequences?: {
    sequence_name?: string;
    messages?: Array<{
      message_number: number;
      content: string;
      character_count: number;
      delay_days?: number;
    }>;
  };
  preset_filter_requests?: Array<{
    id: string;
    request_name: string;
    filters: any;
    target_lead_count?: number;
    priority?: string;
  }>;
}

// Removed unused NotificationData interface - was causing ESLint error

const CampaignApprovals: React.FC = () => {
  const [pendingCampaigns, setPendingCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [selectedFilterModal, setSelectedFilterModal] = useState<Campaign | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState<string | null>(null);

  const loadPendingCampaigns = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get campaigns that are submitted or preparing for this CSR
      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          id, name, status, created_at, submitted_at, total_leads, service_tier,
          lead_source_type, target_audience_type, lead_source_id,
          users!user_id(id, full_name, email),
          campaign_sequences!sequence_id(sequence_name, messages)
        `)
        .eq('assigned_csr', session.user.id)
        .in('status', ['submitted', 'preparing'])
        .order('submitted_at', { ascending: true });
      
      if (error) {
        console.error('Error loading pending campaigns:', error);
        return;
      }

      // Get preset filter requests for campaigns separately
      const campaignIds = data?.map(c => c.id) || [];
      const leadSourceIds = data?.filter(c => c.lead_source_id).map(c => c.lead_source_id) || [];
      
      // Get filters by campaign_id (for Target Leads)
      const { data: presetFiltersByCampaign } = await supabase
        .from('preset_filter_requests')
        .select('*')
        .in('campaign_id', campaignIds);

      // Get filters by id (for Previous Target Lists using lead_source_id)
      const { data: presetFiltersById } = leadSourceIds.length > 0 ? await supabase
        .from('preset_filter_requests')
        .select('*')
        .in('id', leadSourceIds) : { data: [] };

      // Merge preset filter data with campaigns
      const campaignsWithFilters = data?.map(campaign => {
        // For Target Leads (preset_filter), use campaign_id
        let campaignFilters = presetFiltersByCampaign?.filter(f => f.campaign_id === campaign.id) || [];
        
        // For Previous Target Lists (past_list), use lead_source_id
        if (campaign.lead_source_type === 'past_list' && campaign.lead_source_id) {
          const pastListFilter = presetFiltersById?.find(f => f.id === campaign.lead_source_id);
          if (pastListFilter) {
            campaignFilters = [pastListFilter];
          }
        }
        
        return {
          ...campaign,
          preset_filter_requests: campaignFilters
        };
      });

      console.log('Loaded pending campaigns:', campaignsWithFilters);
      // Debug: Check if preset_filter_requests is included
      if (campaignsWithFilters && campaignsWithFilters.length > 0) {
        console.log('First campaign data structure:', {
          lead_source_type: campaignsWithFilters[0].lead_source_type,
          target_audience_type: campaignsWithFilters[0].target_audience_type,
          preset_filter_requests: campaignsWithFilters[0].preset_filter_requests,
          campaign_sequences: campaignsWithFilters[0].campaign_sequences,
          sequence_id: campaignsWithFilters[0].sequence_id
        });
      }
      setPendingCampaigns(campaignsWithFilters || []);
    } catch (error) {
      console.error('Error in loadPendingCampaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingCampaigns();
    
    // Set up real-time subscription for campaign status changes
    const subscription = supabase
      .channel('campaign-approvals')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'campaigns'
        },
        () => loadPendingCampaigns()
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'campaigns'
        },
        () => loadPendingCampaigns()
      )
      .subscribe();

    return () => subscription.unsubscribe();
  }, [loadPendingCampaigns]);

  const approveCampaign = async (campaign: Campaign) => {
    setProcessingId(campaign.id);
    
    console.log('Approving campaign:', campaign.name, 'User data:', campaign.users);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'running',
          approved_at: new Date().toISOString(),
          approved_by: session.user.id,
          locked_at: new Date().toISOString()
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error approving campaign:', error);
        alert('Failed to approve campaign. Please try again.');
        return;
      }

      // Create notification for user (if user data exists)
      if (campaign.users?.id) {
        const { error: notifError } = await supabase
          .from('notifications')
          .insert({
            user_id: campaign.users.id,
            type: 'campaign_approved',
            title: 'Campaign Approved!',
            message: `Your campaign "${campaign.name}" has been approved and is now running.`,
            priority: 'high',  // High priority for campaign approvals
            read: false,
            data: { 
              campaign_id: campaign.id,
              campaign_name: campaign.name,
              action_url: '/campaigns'
            }
          });
        
        if (notifError) {
          console.error('Failed to create approval notification:', notifError);
        }
      }

      // Reload campaigns
      loadPendingCampaigns();
      
      alert('Campaign approved successfully!');
    } catch (error) {
      console.error('Error in approveCampaign:', error);
      alert('Failed to approve campaign. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const requestEdits = async (campaign: Campaign) => {
    setProcessingId(campaign.id);
    
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'draft',
          submitted_at: null
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error requesting edits:', error);
        alert('Failed to request edits. Please try again.');
        return;
      }

      // Create notification for user (if user data exists)
      if (campaign.users?.id) {
        const { error: notifError } = await supabase
          .from('notifications')
          .insert({
            user_id: campaign.users.id,
            type: 'campaign_needs_editing',
            title: 'Campaign Needs Editing',
            message: `Your campaign "${campaign.name}" requires changes before approval. Please review and resubmit.`,
            priority: 'high',  // High priority for edits needed
            read: false,
            data: { 
              campaign_id: campaign.id,
              campaign_name: campaign.name,
              action_url: `/create-campaign-wizard?edit=${campaign.id}`
            }
          });
        
        if (notifError) {
          console.error('Failed to create needs-editing notification:', notifError);
        }
      }

      // Reload campaigns
      loadPendingCampaigns();
      
      alert('Campaign sent back for editing.');
    } catch (error) {
      console.error('Error in requestEdits:', error);
      alert('Failed to request edits. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      submitted: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      running: 'bg-green-100 text-green-800',
      completed: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  // EPOCH 63: Renamed "JUNIOR SDR" to "PLATFORM"
  const getServiceTierBadge = (tier: string) => {
    return tier === 'top_qualifier' ? (
      <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
        TOP QUALIFIER
      </span>
    ) : (
      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
        PLATFORM
      </span>
    );
  };

  const getLeadSourceType = (campaign: Campaign): string => {
    const leadMethod = campaign.lead_source_type;
    const targetType = campaign.target_audience_type;
    
    if (leadMethod === 'preset_filter' || leadMethod === 'preset_filters') {
      return 'Target Lead List';
    } else if (leadMethod === 'past_list') {
      return 'Previous Target List';
    } else if (leadMethod === 'csv_upload') {
      return 'CSV Upload';
    } else if (leadMethod === 'lead_generator') {
      return 'Lead Generator';
    } else if (targetType === 'preset_filter' || targetType === 'preset_filters') {
      return 'Target Lead List';
    } else if (targetType === 'past_list') {
      return 'Previous Target List';
    } else if (targetType === 'csv_upload') {
      return 'CSV Upload';
    }
    
    return 'Not specified';
  };

  const renderLeadSourceAction = (campaign: Campaign) => {
    const leadSourceType = getLeadSourceType(campaign);
    const hasFilterData = campaign.preset_filter_requests && campaign.preset_filter_requests.length > 0;
    
    if ((leadSourceType === 'Target Lead List' || leadSourceType === 'Previous Target List') && hasFilterData) {
      return (
        <button
          onClick={() => handleViewPresetFilter(campaign)}
          className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium shadow-sm"
        >
          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          View Filters
        </button>
      );
    } else if (leadSourceType === 'CSV Upload') {
      return (
        <button
          onClick={() => handleDownloadCSV(campaign)}
          disabled={downloadingCsv === campaign.id}
          className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-medium shadow-sm disabled:opacity-50"
        >
          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          {downloadingCsv === campaign.id ? 'Downloading...' : 'Download CSV'}
        </button>
      );
    }
    return null;
  };

  const handleViewPresetFilter = (campaign: Campaign) => {
    if (campaign.preset_filter_requests && campaign.preset_filter_requests.length > 0) {
      setSelectedFilterModal(campaign);
    } else {
      alert('No filter details available for this campaign');
    }
  };

  const handleDownloadCSV = async (campaign: Campaign) => {
    setDownloadingCsv(campaign.id);
    try {
      // First get the CSV upload info
      const { data: csvData, error: csvError } = await supabase
        .from('campaign_lead_uploads')
        .select('file_path, file_name')
        .eq('campaign_id', campaign.id)
        .single();

      if (csvError || !csvData?.file_path) {
        alert('No CSV file available for this campaign');
        return;
      }

      // Download the file from storage
      const { data, error } = await supabase.storage
        .from('lead-uploads')
        .download(csvData.file_path);

      if (error) throw error;

      // Create a blob and trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = csvData.file_name || 'leads.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV file. Please check your permissions.');
    } finally {
      setDownloadingCsv(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaign Approvals</h1>
        <p className="text-gray-600">
          Review and approve campaigns submitted by your assigned users.
        </p>
      </div>

      {pendingCampaigns.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pending campaigns</h3>
          <p className="text-gray-500">
            There are no campaigns waiting for your approval at the moment.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {pendingCampaigns.map(campaign => (
            <div key={campaign.id} className="bg-white rounded-lg shadow border border-gray-200">
              <div className="p-6">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900">{campaign.name}</h3>
                    <div className="flex items-center space-x-3 mt-1">
                      {getStatusBadge(campaign.status)}
                      {getServiceTierBadge(campaign.service_tier)}
                    </div>
                  </div>
                  
                  {/* Lead Source and Message Details */}
                  <div className="flex items-start space-x-6">
                    <div className="text-right">
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 mb-2">
                        {getLeadSourceType(campaign) === 'Target Lead List' && '🎯'}
                        {getLeadSourceType(campaign) === 'Previous Target List' && '📋'}
                        {getLeadSourceType(campaign) === 'CSV Upload' && '📁'}
                        {getLeadSourceType(campaign) === 'Lead Generator' && '🤖'}
                        <span className="ml-1">{getLeadSourceType(campaign)}</span>
                      </div>
                      <div className="mt-1">
                        {renderLeadSourceAction(campaign)}
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedCampaign === campaign.id ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"} />
                      </svg>
                      {expandedCampaign === campaign.id ? 'Hide' : 'View'} Message Details
                    </button>
                  </div>
                </div>

                {/* Submission Info */}
                <div className="text-sm text-gray-600 space-y-1 mb-4">
                  <p><strong>Submitted by:</strong> {campaign.users?.full_name || 'Unknown User'} ({campaign.users?.email || 'N/A'})</p>
                  <p><strong>Submitted:</strong> {campaign.submitted_at ? formatDate(campaign.submitted_at) : 'N/A'}</p>
                </div>

                {/* Expanded Message Details */}
                {expandedCampaign === campaign.id && campaign.campaign_sequences?.messages && (
                  <MessageSequenceDisplay 
                    messages={campaign.campaign_sequences.messages}
                    title="LinkedIn Message Sequence"
                    className="mt-4"
                  />
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => requestEdits(campaign)}
                    disabled={processingId === campaign.id}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingId === campaign.id ? 'Processing...' : 'Request Edits'}
                  </button>
                  
                  <button
                    onClick={() => approveCampaign(campaign)}
                    disabled={processingId === campaign.id}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingId === campaign.id ? 'Processing...' : 'Approve & Launch'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Preset Filter Modal */}
      {selectedFilterModal && selectedFilterModal.preset_filter_requests && selectedFilterModal.preset_filter_requests.length > 0 && (
        <PresetFilterModal
          isOpen={!!selectedFilterModal}
          onClose={() => setSelectedFilterModal(null)}
          filterData={selectedFilterModal.preset_filter_requests[0]}
          campaignName={selectedFilterModal.name}
        />
      )}
    </div>
  );
};

export default CampaignApprovals;